import { ProviderWithProducts } from "../../../../hooks/useProviders";

interface Props {
  provider: ProviderWithProducts;
}

export default function ProviderPhilosophySection({ provider }: Props) {
  return (
    <section id="philosophy" className="py-14 px-4 md:px-0">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        {/* Philosophy main */}
        <div className="lg:col-span-2">
          <div className="mb-3">
            <p className="text-gold text-xs font-display italic tracking-widest mb-1">Philosophy</p>
            <h2 className="font-serif text-deep-green text-2xl md:text-3xl font-light">理念・想い</h2>
          </div>

          {provider.slogan && (
            <p className="font-display italic text-teal text-lg md:text-xl mb-6 leading-relaxed">
              &ldquo;{provider.slogan}&rdquo;
            </p>
          )}

          {provider.philosophy ? (
            <p className="text-charcoal/70 font-sans text-sm md:text-base leading-relaxed mb-8 whitespace-pre-wrap">
              {provider.philosophy}
            </p>
          ) : provider.bio ? (
            <p className="text-charcoal/70 font-sans text-sm md:text-base leading-relaxed mb-8 whitespace-pre-wrap">
              {provider.bio}
            </p>
          ) : (
            <p className="text-charcoal/40 font-sans text-sm mb-8">
              この事業者はまだ理念を登録していません。
            </p>
          )}

          {/* Skills callout */}
          {provider.skills && provider.skills.length > 0 && (
            <div className="bg-deep-green/5 border-l-4 border-deep-green/30 rounded-r-xl p-6">
              <div className="flex gap-3 items-start">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-deep-green/10 flex-shrink-0 mt-0.5">
                  <i className="ri-award-line text-deep-green text-lg" />
                </div>
                <div>
                  <p className="font-serif text-deep-green text-base mb-2">このサービスで学べること</p>
                  <p className="text-charcoal/60 text-sm font-sans leading-relaxed">
                    {provider.service_name || provider.name}では、{provider.skills.join("・")}に関する専門技術を動画講座と教材キットで体系的に習得できます。
                    購入後は講師への直接メッセージで疑問を解消しながら、着実にスキルアップできます。
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Company overview sidebar */}
        <div className="bg-ivory rounded-2xl p-6 h-fit">
          <p className="text-gold text-xs font-display italic tracking-widest mb-4">Profile</p>
          <h3 className="font-serif text-deep-green text-base mb-4">事業者情報</h3>
          <dl className="flex flex-col gap-3 text-sm">
            <div className="flex flex-col gap-0.5">
              <dt className="text-charcoal/40 font-sans text-xs">事業者名</dt>
              <dd className="font-sans text-charcoal font-medium">{provider.name}</dd>
            </div>
            {provider.service_name && (
              <div className="flex flex-col gap-0.5">
                <dt className="text-charcoal/40 font-sans text-xs">サービス名</dt>
                <dd className="font-sans text-charcoal">{provider.service_name}</dd>
              </div>
            )}
            {provider.location && (
              <div className="flex flex-col gap-0.5">
                <dt className="text-charcoal/40 font-sans text-xs">活動エリア</dt>
                <dd className="font-sans text-charcoal flex items-center gap-1">
                  <i className="ri-map-pin-line text-teal text-xs" />
                  {provider.location}
                </dd>
              </div>
            )}
            {provider.category && (
              <div className="flex flex-col gap-0.5">
                <dt className="text-charcoal/40 font-sans text-xs">専門カテゴリ</dt>
                <dd className="font-sans text-charcoal">{provider.category}</dd>
              </div>
            )}
            {provider.employee_count && (
              <div className="flex flex-col gap-0.5">
                <dt className="text-charcoal/40 font-sans text-xs">従業員数</dt>
                <dd className="font-sans text-charcoal">{provider.employee_count}</dd>
              </div>
            )}
            {provider.capital_amount && (
              <div className="flex flex-col gap-0.5">
                <dt className="text-charcoal/40 font-sans text-xs">資本金</dt>
                <dd className="font-sans text-charcoal">{provider.capital_amount}</dd>
              </div>
            )}
            {provider.service_content && (
              <div className="flex flex-col gap-0.5">
                <dt className="text-charcoal/40 font-sans text-xs">サービス内容</dt>
                <dd className="font-sans text-charcoal/70 leading-relaxed text-xs">{provider.service_content}</dd>
              </div>
            )}
            {provider.company_overview && (
              <div className="flex flex-col gap-0.5">
                <dt className="text-charcoal/40 font-sans text-xs">会社概要</dt>
                <dd className="font-sans text-charcoal/70 leading-relaxed text-xs">{provider.company_overview}</dd>
              </div>
            )}
          </dl>

          {/* Rating summary */}
          <div className="mt-6 pt-6 border-t border-stone-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-charcoal/50 font-sans">総合評価</span>
              <div className="flex items-center gap-1">
                <span className="text-gold text-sm">★</span>
                <strong className="text-deep-green font-sans text-lg">
                  {provider.rating ? provider.rating.toFixed(1) : "—"}
                </strong>
              </div>
            </div>
            <p className="text-xs text-charcoal/40 font-sans">{provider.review_count ?? 0}件のレビューより</p>
          </div>
        </div>
      </div>
    </section>
  );
}
